import { useAppDispatch, useAppSelector } from "hooks/redux";
import { cloneDeep } from "lodash";
import { useWebsiteBuilderDebouncedUpdate, WebsiteBuilderDebouncedUpdateTime } from "pages/secure/facility/websiteBuilder/editor/hooks/debouncedUpdate/useWebsiteBuilderDebouncedUpdate";
import React, { useState } from "react";
import { websiteBuilderUpdateElementSettings } from "redux/actions/websiteBuilder/editor/websiteBuilderEditor";
import { IWebsiteBuilderVideoSettings } from "pages/secure/facility/websiteBuilder/editor/nodes/element/websiteBuilderVideo";
import { ButtonNew as Button } from "components/buttonNew/index";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faVideo } from "@fortawesome/pro-regular-svg-icons";
import useModal from "hooks/modals/useModal";
import { IWebsiteBuilderMediaItem } from "redux/reducers/models/websiteBuilder";
import { useTranslation } from "react-i18next";
import { WebsiteBuilderSelectMedia } from "../../../../../helpers/WebsiteBuilderSelectMedia";
import Checkbox from "components/form/checkbox/Checkbox";
import Input from "components/form/input/index";
import { WebsiteBuilderPanelContent } from "pages/secure/facility/websiteBuilder/editor/shell/panel/content/WebsiteBuilderPanelContent";

interface IVideoSettingsProps {
  id: string;
  settings: IWebsiteBuilderVideoSettings;
  closeSettings: () => void;
}

export function VideoSettings(props: IVideoSettingsProps) {
  const { id, settings, closeSettings } = props;

  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const activePageId = useAppSelector(store => store.websiteBuilderStore.editor.activePageId);
  const deviceView = useAppSelector(store => store.websiteBuilderStore.editor.deviceView);

  const [updatedSettings, setUpdatedSettings] = useState<IWebsiteBuilderVideoSettings>(() => cloneDeep(settings));

  const { state: selectVideoModalState, updateModal: updateSelectVideoModal, closeModal: closeSelectVideoModal } = useModal();

  function dispatchSettings(prevSettings: IWebsiteBuilderVideoSettings, nextSettings: IWebsiteBuilderVideoSettings) {
    dispatch(
      websiteBuilderUpdateElementSettings(activePageId, deviceView, id, {
        id,
        prevElementSettings: {
          type: "video",
          settings: cloneDeep(prevSettings),
        },
        nextElementSettings: {
          type: "video",
          settings: cloneDeep(nextSettings),
        },
      }),
    );
  }

  const { handleUpdateState, handleDebouncedUpdateState } = useWebsiteBuilderDebouncedUpdate({
    state: settings,
    updatedState: updatedSettings,
    setUpdatedState: setUpdatedSettings,
    dispatchState: dispatchSettings,
    debouncedUpdateTime: WebsiteBuilderDebouncedUpdateTime.MEDIUM,
  });

  function handleOpenSelectVideoModal() {
    updateSelectVideoModal({ isOpen: true });
  }

  function handleCloseSelectVideoModal() {
    closeSelectVideoModal();
  }

  function handleSelectVideo(video: IWebsiteBuilderMediaItem) {
    handleUpdateState(prev => ({
      ...prev,
      mediaId: video.id,
      src: video.source,
    }));

    handleCloseSelectVideoModal();
  }

  function renderSettings() {
    return (
      <>
        <div className="website-builder-video-settings-playback-title">{t("Playback options")}</div>
        <Checkbox
          size="medium"
          label={t("Autoplay")}
          checked={updatedSettings.autoPlay}
          onChange={e =>
            handleUpdateState(prev => ({
              ...prev,
              autoPlay: e.target.checked,
              muted: e.target.checked ? true : prev.muted,
            }))
          }
        />
        <Checkbox
          size="medium"
          label={t("Muted")}
          checked={updatedSettings.muted}
          disabled={updatedSettings.autoPlay}
          onChange={e =>
            handleUpdateState(prev => ({
              ...prev,
              muted: e.target.checked,
            }))
          }
        />
        <Checkbox
          size="medium"
          label={t("Loop")}
          checked={updatedSettings.loop}
          onChange={e =>
            handleUpdateState(prev => ({
              ...prev,
              loop: e.target.checked,
            }))
          }
        />
        <Input
          label={t("Description")}
          value={updatedSettings.description}
          onChange={e => {
            handleDebouncedUpdateState(prev => ({
              ...prev,
              description: e.target.value,
            }));
          }}
        />
        <Button
          size="small"
          type="secondary"
          block
          onClick={handleOpenSelectVideoModal}
        >
          <FontAwesomeIcon icon={faVideo} />&nbsp;{t("Select video")}
        </Button>

        <WebsiteBuilderSelectMedia
          type="video"
          mediaId={updatedSettings.mediaId}
          isOpen={selectVideoModalState.isOpen}
          closeModal={handleCloseSelectVideoModal}
          handleSelectMedia={handleSelectVideo}
        />
      </>
    );
  }

  return (
    <WebsiteBuilderPanelContent
      title={t("Video settings")}
      closePanel={closeSettings}
      sections={[renderSettings()]}
    />
  );
}
